import React, { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Menu } from '@headlessui/react';
import { Check, ChevronDown, LogIn, Search, ShieldCheck } from 'lucide-react';
import { clsx } from 'clsx';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { ConfirmationModal } from '../../components/ui/Modal';
import { platformAdminApi, type AdminUser } from '../../services/platform-admin-api';
import AdminUserDrawer from './AdminUserDrawer';
import StatusPill from './StatusPill';
import { formatDate, initials } from './format';

const PAGE_SIZE = 50;

const STATUSES = [
  { id: '', label: 'Any status' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'suspended', label: 'Suspended' },
  { id: 'pending_invitation', label: 'Pending invitation' },
];

const TIERS = [
  { id: '', label: 'Any plan' },
  { id: 'free', label: 'Free' },
  { id: 'starter', label: 'Starter' },
  { id: 'business', label: 'Business' },
  { id: 'scale', label: 'Scale' },
];

const PROVIDERS = [
  { id: '', label: 'Any sign-in' },
  { id: 'email', label: 'Email + password' },
  { id: 'google', label: 'Google' },
  { id: 'apple', label: 'Apple' },
];

const AdminUsersPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  const [status, setStatus] = useState('');
  const [tier, setTier] = useState('');
  const [authProvider, setAuthProvider] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [staffOnly, setStaffOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [impersonating, setImpersonating] = useState<AdminUser | null>(null);
  const [starting, setStarting] = useState(false);

  const filters = { search: debouncedSearch, status, tier, authProvider, workspaceId, staffOnly };

  const list = useInfiniteQuery({
    queryKey: ['platformAdminUsers', filters],
    queryFn: ({ pageParam }) =>
      platformAdminApi.listUsers({
        search: debouncedSearch || undefined,
        status: status || undefined,
        tier: tier || undefined,
        authProvider: authProvider || undefined,
        workspaceId: workspaceId || undefined,
        staffOnly: staffOnly || undefined,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
  });

  const { data: workspaces } = useQuery({
    queryKey: ['platformAdminWorkspaces'],
    queryFn: () => platformAdminApi.listWorkspaces(),
    staleTime: 5 * 60 * 1000,
  });

  const users = useMemo(() => (list.data?.pages ?? []).flatMap((p) => p.users), [list.data]);
  const total = list.data?.pages[0]?.total ?? 0;

  const handleImpersonate = async () => {
    if (!impersonating) return;
    setStarting(true);
    try {
      // Navigates away on success — no need to reset state.
      await platformAdminApi.impersonate(impersonating.id);
    } catch {
      setStarting(false);
      setImpersonating(null);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 pt-7 pb-24">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Users</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Every account across all workspaces. {total.toLocaleString()} total.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-[12px] font-semibold text-primary">
          <ShieldCheck className="h-3.5 w-3.5" />
          Platform admin
        </span>
      </div>

      {/* Toolbar */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, or user ID"
            className="w-[290px] rounded-[10px] border border-input bg-card py-1.5 pl-9 pr-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
          />
        </div>
        <ChipMenu
          label={STATUSES.find((s) => s.id === status)?.label ?? 'Any status'}
          active={!!status}
          options={STATUSES}
          selected={status}
          onSelect={setStatus}
        />
        <ChipMenu
          label={TIERS.find((t) => t.id === tier)?.label ?? 'Any plan'}
          active={!!tier}
          options={TIERS}
          selected={tier}
          onSelect={setTier}
        />
        <ChipMenu
          label={PROVIDERS.find((p) => p.id === authProvider)?.label ?? 'Any sign-in'}
          active={!!authProvider}
          options={PROVIDERS}
          selected={authProvider}
          onSelect={setAuthProvider}
        />
        <ChipMenu
          label={
            workspaceId
              ? (workspaces ?? []).find((w) => w.id === workspaceId)?.name ?? 'Workspace'
              : 'Any workspace'
          }
          active={!!workspaceId}
          options={[
            { id: '', label: 'Any workspace' },
            ...(workspaces ?? []).map((w) => ({ id: w.id, label: w.name })),
          ]}
          selected={workspaceId}
          onSelect={setWorkspaceId}
        />
        <button
          onClick={() => setStaffOnly((v) => !v)}
          className={clsx(
            'rounded-[10px] border px-3 py-1.5 text-[12.5px] font-semibold',
            staffOnly
              ? 'border-primary/40 bg-accent text-primary'
              : 'border-input bg-card text-foreground hover:bg-accent',
          )}
        >
          Staff only
        </button>
      </div>

      {/* Table */}
      <div className="mt-3.5 overflow-x-auto rounded-[10px] border border-border bg-card shadow-sm">
        {list.isLoading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner />
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">
            No users match these filters.
          </div>
        ) : (
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr>
                {['User', 'Status', 'Workspaces', 'Sign-in', 'Joined', 'Last login', ''].map((h) => (
                  <th
                    key={h}
                    className="whitespace-nowrap border-b border-border px-3.5 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  onSelect={() => setSelectedId(u.id)}
                  onImpersonate={() => setImpersonating(u)}
                />
              ))}
            </tbody>
          </table>
        )}
        {list.hasNextPage && (
          <div className="flex justify-center border-t border-border py-3">
            <button
              onClick={() => void list.fetchNextPage()}
              disabled={list.isFetchingNextPage}
              className="rounded-[10px] border border-input bg-card px-4 py-1.5 text-[13px] font-medium text-foreground hover:bg-accent disabled:opacity-50"
            >
              {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>

      <AdminUserDrawer
        userId={selectedId}
        onClose={() => setSelectedId(null)}
        onImpersonate={(u) => {
          setSelectedId(null);
          setImpersonating(u);
        }}
      />

      <ConfirmationModal
        isOpen={!!impersonating}
        onClose={() => setImpersonating(null)}
        onConfirm={handleImpersonate}
        isLoading={starting}
        title="Log in as this user?"
        confirmText="Log in as user"
        message={
          impersonating
            ? [
                impersonating.workspaces.length === 0
                  ? `Heads up: ${impersonating.email} has no active workspace membership, so you will land on the create-workspace screen. That is genuinely what this user sees when they log in — not a fault in the portal.`
                  : '',
                `You will be signed in as ${impersonating.email} with their full access for up to 1 hour. This is recorded in the audit log against your account, and every action you take will be attributed to you.`,
              ]
                .filter(Boolean)
                .join('\n\n')
            : ''
        }
      />
    </div>
  );
};

// ── Row ─────────────────────────────────────────────────────────────────

const UserRow: React.FC<{
  user: AdminUser;
  onSelect: () => void;
  onImpersonate: () => void;
}> = ({ user, onSelect, onImpersonate }) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  const locked = !!user.lockedUntil && new Date(user.lockedUntil).getTime() > Date.now();
  const canImpersonate = user.status === 'active' && !user.platformRole;

  return (
    <tr className="cursor-pointer hover:bg-muted/50" onClick={onSelect}>
      <td className="border-b border-border px-3.5 py-2.5 align-top">
        <div className="flex items-center gap-2.5">
          <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary text-[10px] font-extrabold text-primary-foreground">
            {initials(name || user.email)}
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-1.5">
              <span className="font-semibold text-foreground">{name || '—'}</span>
              {user.platformRole && (
                <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-primary">
                  Staff
                </span>
              )}
              {user.isInternalTester && (
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-muted-foreground">
                  Tester
                </span>
              )}
            </span>
            <div className="text-[11px] text-muted-foreground">{user.email}</div>
          </span>
        </div>
      </td>

      <td className="whitespace-nowrap border-b border-border px-3.5 py-2.5 align-top">
        <StatusPill status={locked ? 'locked' : user.status} />
        {user.deletionScheduledAt && (
          <div className="mt-1 text-[10.5px] text-destructive">Deletion scheduled</div>
        )}
      </td>

      <td className="border-b border-border px-3.5 py-2.5 align-top">
        {user.workspaces.length === 0 ? (
          // Orphan: a stale users.last_active_workspace_id makes these look
          // connected in the DB, but they land on the create-workspace wizard.
          <span
            className="inline-flex rounded-full bg-warn/15 px-2 py-0.5 text-[10.5px] font-semibold text-warn"
            title="No active workspace membership — this user sees the create-workspace screen when they log in"
          >
            No workspace
          </span>
        ) : (
          <div className="space-y-0.5">
            {user.workspaces.slice(0, 2).map((w) => (
              <div key={w.id} className="whitespace-nowrap">
                <span className="font-medium text-foreground">{w.name}</span>
                <span className="text-muted-foreground">
                  {' '}
                  · {w.tier} · {w.role.replace('workspace-', '')}
                </span>
              </div>
            ))}
            {user.workspaces.length > 2 && (
              <div className="text-[11px] text-muted-foreground">
                +{user.workspaces.length - 2} more
              </div>
            )}
          </div>
        )}
      </td>

      <td className="whitespace-nowrap border-b border-border px-3.5 py-2.5 align-top">
        <div className="capitalize text-foreground">{user.authProvider}</div>
        <div className="text-[10.5px] text-muted-foreground">
          {user.mfaEnabled ? '2FA on' : '2FA off'}
          {!user.emailVerified && ' · unverified'}
        </div>
      </td>

      <td className="whitespace-nowrap border-b border-border px-3.5 py-2.5 align-top text-muted-foreground">
        {formatDate(user.createdAt)}
      </td>

      <td className="whitespace-nowrap border-b border-border px-3.5 py-2.5 align-top text-muted-foreground">
        {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
      </td>

      <td className="whitespace-nowrap border-b border-border px-3.5 py-2.5 align-top text-right">
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (!canImpersonate) return;
            onImpersonate();
          }}
          disabled={!canImpersonate}
          title={
            user.platformRole
              ? 'Cannot impersonate another platform admin'
              : user.status !== 'active'
                ? `Cannot impersonate a ${user.status} account`
                : 'Sign in as this user'
          }
          className="inline-flex items-center gap-1.5 rounded-[10px] border border-input bg-card px-2.5 py-1 text-[12px] font-semibold text-foreground hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
        >
          <LogIn className="h-3.5 w-3.5" />
          Log in as
        </button>
      </td>
    </tr>
  );
};

const ChipMenu: React.FC<{
  label: string;
  active?: boolean;
  options: Array<{ id: string; label: string }>;
  selected: string;
  onSelect: (id: string) => void;
}> = ({ label, active, options, selected, onSelect }) => (
  <Menu as="div" className="relative">
    <Menu.Button
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-[10px] border px-3 py-1.5 text-[12.5px]',
        active
          ? 'border-primary/40 bg-accent font-semibold text-primary'
          : 'border-input bg-card text-foreground hover:bg-accent',
      )}
    >
      {label} <ChevronDown className="h-3.5 w-3.5" />
    </Menu.Button>
    <Menu.Items className="absolute left-0 top-9 z-20 max-h-72 min-w-[190px] overflow-y-auto rounded-[10px] border border-border bg-card p-1 shadow-lg focus:outline-none">
      {options.map((o) => (
        <Menu.Item key={o.id}>
          {({ active: hovered }) => (
            <button
              onClick={() => onSelect(o.id)}
              className={clsx(
                'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[13px]',
                hovered && 'bg-muted',
                o.id === selected ? 'font-medium text-primary' : 'text-foreground',
              )}
            >
              <span className="flex-1 truncate">{o.label}</span>
              {o.id === selected && <Check className="h-3.5 w-3.5" />}
            </button>
          )}
        </Menu.Item>
      ))}
    </Menu.Items>
  </Menu>
);

// ── Helpers ─────────────────────────────────────────────────────────────

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export default AdminUsersPage;
